import type {
  HistoricalAnalysis,
  AnalysisResult,
  FollowersData,
  FollowingData,
  ComparisonResult,
  RenamedFollower
} from '@/types/instagram';
import {
  collectNormalizedFollowers,
  collectNormalizedFollowing,
  normalizeForCompare,
  type NormalizedInstagramUser
} from '@/utils/analysis';

const buildNormalizedMap = (users: NormalizedInstagramUser[]) => {
  const map = new Map<string, NormalizedInstagramUser>();
  for (const user of users) {
    if (!map.has(user.normalized)) {
      map.set(user.normalized, user);
    }
  }
  return map;
};

const buildTimestampMap = (users: NormalizedInstagramUser[]) => {
  const map = new Map<number, NormalizedInstagramUser[]>();
  for (const user of users) {
    if (typeof user.timestamp !== 'number') continue;
    const list = map.get(user.timestamp) ?? [];
    list.push(user);
    map.set(user.timestamp, list);
  }
  return map;
};

export const computeComparison = (
  historicalAnalyses: HistoricalAnalysis[],
  currentResult: AnalysisResult,
  currentFollowersData?: FollowersData | { relationships_followers: FollowersData },
  currentFollowingData?: FollowingData
): ComparisonResult | null => {
  if (historicalAnalyses.length === 0) {
    return null;
  }

  const previousAnalysis = historicalAnalyses[0];

  const prevFollowersNormalized = previousAnalysis.followers_data
    ? collectNormalizedFollowers(previousAnalysis.followers_data)
    : [];
  const prevFollowersMap = buildNormalizedMap(prevFollowersNormalized);

  const prevFollowingNormalized = previousAnalysis.following_data
    ? collectNormalizedFollowing(previousAnalysis.following_data)
    : [];
  const prevFollowingMap = buildNormalizedMap(prevFollowingNormalized);

  const currFollowersNormalized = currentFollowersData
    ? collectNormalizedFollowers(currentFollowersData)
    : (currentResult.followersList ?? []).map(username => {
        const normalized = normalizeForCompare(username);
        return normalized
          ? { username, normalized }
          : null;
      }).filter(Boolean) as NormalizedInstagramUser[];
  const currFollowersMap = buildNormalizedMap(currFollowersNormalized);

  const currFollowingNormalized = currentFollowingData
    ? collectNormalizedFollowing(currentFollowingData)
    : (currentResult.followingList ?? []).map(username => {
        const normalized = normalizeForCompare(username);
        return normalized
          ? { username, normalized }
          : null;
      }).filter(Boolean) as NormalizedInstagramUser[];
  const currFollowingMap = buildNormalizedMap(currFollowingNormalized);

  const newFollowersMap = new Map<string, NormalizedInstagramUser>();
  for (const follower of currFollowersNormalized) {
    if (!prevFollowersMap.has(follower.normalized)) {
      newFollowersMap.set(follower.normalized, follower);
    }
  }

  const lostFollowersMap = new Map<string, NormalizedInstagramUser>();
  for (const follower of prevFollowersNormalized) {
    if (!currFollowersMap.has(follower.normalized)) {
      lostFollowersMap.set(follower.normalized, follower);
    }
  }

  const prevTimestampMap = buildTimestampMap(prevFollowersNormalized);
  const currTimestampMap = buildTimestampMap(currFollowersNormalized);
  const renamedFollowers: RenamedFollower[] = [];

  for (const [timestamp, currUsers] of currTimestampMap.entries()) {
    if (currUsers.length !== 1) continue;
    const prevUsers = prevTimestampMap.get(timestamp);
    if (!prevUsers || prevUsers.length !== 1) continue;

    const currUser = currUsers[0];
    const prevUser = prevUsers[0];

    if (currUser.normalized === prevUser.normalized) continue;
    if (!newFollowersMap.has(currUser.normalized)) continue;
    if (!lostFollowersMap.has(prevUser.normalized)) continue;

    renamedFollowers.push({
      previousUsername: prevUser.username,
      currentUsername: currUser.username
    });

    newFollowersMap.delete(currUser.normalized);
    lostFollowersMap.delete(prevUser.normalized);
  }

  const newFollowersList = Array.from(newFollowersMap.values());
  const lostFollowersList = Array.from(lostFollowersMap.values());

  const currFollowingSet = new Set(currFollowingNormalized.map(user => user.normalized));

  const newFollowersTotal = newFollowersList.map(user => user.username);
  const newFollowersFollowed = newFollowersList
    .filter(user => currFollowingSet.has(user.normalized))
    .map(user => user.username);
  const newFollowers = newFollowersList
    .filter(user => !currFollowingSet.has(user.normalized))
    .map(user => user.username);

  const lostFollowers = lostFollowersList.map(user => user.username);

  const prevFollowingSet = new Set(prevFollowingNormalized.map(user => user.normalized));
  const newFollowedMap = new Map<string, NormalizedInstagramUser>();
  for (const user of currFollowingNormalized) {
    if (!prevFollowingSet.has(user.normalized)) {
      newFollowedMap.set(user.normalized, user);
    }
  }

  const prevFollowingTimestampMap = buildTimestampMap(prevFollowingNormalized);
  const currFollowingTimestampMap = buildTimestampMap(currFollowingNormalized);

  for (const [timestamp, currUsers] of currFollowingTimestampMap.entries()) {
    if (currUsers.length !== 1) continue;
    const prevUsers = prevFollowingTimestampMap.get(timestamp);
    if (!prevUsers || prevUsers.length !== 1) continue;

    const currUser = currUsers[0];
    const prevUser = prevUsers[0];

    if (currUser.normalized === prevUser.normalized) continue;
    if (!newFollowedMap.has(currUser.normalized)) continue;

    // Remove renamed accounts from "new followed" to avoid counting name changes.
    newFollowedMap.delete(currUser.normalized);
  }

  const newFollowed = Array.from(newFollowedMap.values()).map(user => user.username);

  const previousNotFollowingYouBackSet = new Set(previousAnalysis.not_following_you_back);
  const currentNotFollowingYouBackSet = new Set(currentResult.notFollowingYouBack);
  const currentNotFollowingBackSet = new Set(currentResult.notFollowingBack);

  const newUnfollowers = currentResult.notFollowingYouBack.filter(
    username => !previousNotFollowingYouBackSet.has(username)
  );

  const unfollowedBack = previousAnalysis.not_following_you_back.filter(
    username => !currentNotFollowingYouBackSet.has(username)
  );

  const followedBack = previousAnalysis.not_following_back.filter(
    username => !currentNotFollowingBackSet.has(username)
  );

  return {
    newUnfollowers,
    newFollowers,
    newFollowersFollowed,
    newFollowersTotal,
    lostFollowers,
    newFollowed,
    unfollowedBack,
    followedBack,
    renamedFollowers,
    previousAnalysis,
  };
};
